/**
 * Hook for batch-fetching category URLs
 *
 * Given category cards, extracts cards with link_type='category',
 * collects unique category IDs, and fetches their URL paths
 * in a single GraphQL query (prevents N+1).
 */

import { useQuery } from '@tanstack/react-query'

import { STATIC_DATA_STALE_TIME, STATIC_DATA_GC_TIME } from '../../constants'
import { homepageApi } from '../../lib'
import { homepageKeys } from '../queryKeys'

/**
 * Minimal shape required by useCategoryUrls.
 * Both HomepageCategoryCard and HomepageBrandCard satisfy this interface.
 */
interface CardWithLink {
  link_type: string
  link_value: string
}

/**
 * Fetches category URLs for cards that link to categories.
 *
 * Builds full URLs from url_path + url_suffix (e.g. "42" → "/bormasini.html")
 *
 * @returns Map of categoryId → full URL (e.g. "42" → "/bormasini.html")
 */
export const useCategoryUrls = (cards: CardWithLink[]) => {
  const categoryIds = [
    ...new Set(
      cards
        .filter((card) => card.link_type === 'category' && card.link_value)
        .map((card) => card.link_value)
    ),
  ]

  const { data: categoryUrlMap = new Map<string, string>(), isLoading } = useQuery({
    queryKey: homepageKeys.categoryUrls(categoryIds),
    queryFn: async () => {
      const result = await homepageApi.getCategoryUrls(categoryIds)
      const urlMap = new Map<string, string>()
      for (const category of result.categoryList) {
        const suffix = category.url_suffix ?? '.html'
        urlMap.set(String(category.id), `/${category.url_path}${suffix}`)
      }
      return urlMap
    },
    enabled: categoryIds.length > 0,
    staleTime: STATIC_DATA_STALE_TIME,
    gcTime: STATIC_DATA_GC_TIME,
  })

  return { categoryUrlMap, isLoading }
}
